/* ============================================================
   BBW · Cart Drawer controller
   Talks to Shopify's AJAX Cart (/cart/add.js, /cart/change.js)
   and refreshes the drawer via the Section Rendering API.

   Sync model (fixes raciness, slow removes, image flashing):
   - SEQUENCING  every request takes a token; only the latest
     response may touch the DOM, so a stale render can never
     resurrect a removed item.
   - MORPHING    server HTML is reconciled per line-item key —
     existing item nodes (and their <img>s) are reused, only
     changed text is patched → no flash on add/resync.
   - OPTIMISTIC  qty/remove update the row instantly; requests
     are serialized per line and rapid clicks collapse into a
     single request carrying the final quantity.
   - RESYNC      after any change, re-fetch at 650/1400/2600 ms
     so server-side gift/discount apps settle; a resync that
     brings identical content is a no-op (morph diffs nothing).
   ============================================================ */
(function () {
  var SECTION = 'bbw-cart-drawer';

  var seq = 0;              // response-ordering token
  var resyncTimers = [];
  var lines = {};           // key -> { desired, inFlight, sendTimer }

  function root() { return document.querySelector('[data-bbw-cart]'); }
  function contentEl() { var r = root(); return r ? r.querySelector('[data-cart-content]') : null; }

  function open() {
    var r = root();
    if (!r) return;
    r.classList.add('is-open');
    r.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bbw-cart-lock');
  }
  function close() {
    var r = root();
    if (!r) return;
    r.classList.remove('is-open');
    r.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bbw-cart-lock');
  }

  function updateCount(n) {
    if (n === undefined || n === null) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
    });
  }

  function hasPendingOps() {
    for (var k in lines) if (lines[k] && (lines[k].inFlight || lines[k].sendTimer)) return true;
    return false;
  }

  // Tell the rest of the ecosystem (gift/discount apps, legacy listeners) that
  // the cart changed — the legacy cart did this too. Tagged so our own
  // cart:update listener can ignore it (no self-loop).
  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'bbw-cart' } }));
    } catch (e) { /* older browsers: skip */ }
  }

  /* ── Morph: reconcile server HTML into the live drawer ─────
     Item nodes are keyed on data-key and REUSED: unchanged rows
     aren't touched at all; changed rows get only their qty text
     and price block patched (media <img> is never recreated).
     Non-item regions (header, footer, empty state) are small and
     imageless, so they're replaced wholesale when they differ. */
  function patchItem(oldItem, newItem) {
    if (oldItem.outerHTML === newItem.outerHTML) return;
    var pairs = [
      ['.bbw-cart__qty-num', 'text'],
      ['.bbw-cart__item-price', 'html'],
      ['.bbw-cart__item-info', 'html'],
      ['.bbw-cart__gift-badge', 'presence']
    ];
    pairs.forEach(function (p) {
      var o = oldItem.querySelector(p[0]);
      var n = newItem.querySelector(p[0]);
      if (p[1] === 'presence') {
        if (o && !n) o.parentNode.removeChild(o);
        else if (!o && n) {
          var top = oldItem.querySelector('.bbw-cart__item-top');
          if (top) top.appendChild(n.cloneNode(true));
        }
        return;
      }
      if (!o || !n) return;
      if (p[1] === 'text') { if (o.textContent !== n.textContent) o.textContent = n.textContent; }
      else { if (o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML; }
    });
  }

  function morph(html) {
    var cur = contentEl();
    if (!cur || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-cart-content]');
    if (!next) return;

    var curList = cur.querySelector('.bbw-cart__items');
    var nextList = next.querySelector('.bbw-cart__items');

    // Structure changed (filled <-> empty): replace everything once.
    if (!curList || !nextList) {
      if (cur.innerHTML !== next.innerHTML) cur.innerHTML = next.innerHTML;
      return;
    }

    // 1) Items — keyed reconciliation.
    var oldByKey = {};
    curList.querySelectorAll('[data-cart-item]').forEach(function (el) {
      oldByKey[el.getAttribute('data-key')] = el;
    });
    var newItems = nextList.querySelectorAll('[data-cart-item]');
    var cursor = null; // last placed node, for ordering
    newItems.forEach(function (newItem) {
      var key = newItem.getAttribute('data-key');
      var node = oldByKey[key];
      if (node) {
        patchItem(node, newItem);
        node.classList.remove('is-removing');
        delete oldByKey[key];
      } else {
        node = newItem.cloneNode(true);
      }
      // Ensure order matches server order without re-inserting settled nodes.
      var expectedPrev = cursor;
      var actualPrev = node.previousElementSibling;
      if (node.parentNode !== curList || actualPrev !== expectedPrev) {
        if (expectedPrev && expectedPrev.nextSibling) curList.insertBefore(node, expectedPrev.nextSibling);
        else if (expectedPrev) curList.appendChild(node);
        else curList.insertBefore(node, curList.firstChild);
      }
      cursor = node;
    });
    // Remove rows the server no longer has.
    for (var k in oldByKey) {
      if (oldByKey[k].parentNode) oldByKey[k].parentNode.removeChild(oldByKey[k]);
    }

    // 2) Header + footer + anything else: swap only when different.
    ['.bbw-cart__header', '.bbw-cart__footer'].forEach(function (sel) {
      var o = cur.querySelector(sel);
      var n = next.querySelector(sel);
      if (o && n && o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML;
    });
  }

  function applySections(sections) {
    if (sections && sections[SECTION]) morph(sections[SECTION]);
  }

  /* ── Re-sync: let server-side gift/discount apps settle ────
     Applies only if it is still the newest request AND no user
     edit is mid-flight (never stomp optimistic state). */
  function resyncOnce() {
    if (hasPendingOps()) { scheduleResync(); return; }
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq && !hasPendingOps()) applySections(sections); })
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (my === seq && cart) updateCount(cart.item_count); })
      .catch(function () {});
  }
  function scheduleResync() {
    resyncTimers.forEach(clearTimeout);
    resyncTimers = [650, 1400, 2600].map(function (delay) {
      return window.setTimeout(resyncOnce, delay);
    });
  }

  /* ── Quantity changes: optimistic, serialized per line ───── */
  function itemEl(key) {
    var r = root();
    return r ? r.querySelector('[data-cart-item][data-key="' + key + '"]') : null;
  }

  function renderOptimistic(key) {
    var st = lines[key];
    var el = itemEl(key);
    if (!st || !el) return;
    var num = el.querySelector('.bbw-cart__qty-num');
    if (num) num.textContent = st.desired;
    el.classList.toggle('is-removing', st.desired === 0);
    el.classList.add('is-busy');
  }

  function sendLine(key) {
    var st = lines[key];
    if (!st || st.inFlight) return;
    st.inFlight = true;
    var sending = st.desired;
    var my = ++seq;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: sending,
        sections: SECTION,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        st.inFlight = false;
        if (st.desired !== sending) { sendLine(key); return; } // user kept clicking — send final value
        delete lines[key];
        var el = itemEl(key);
        if (el) el.classList.remove('is-busy');
        if (my === seq) {
          if (cart && cart.sections) applySections(cart.sections);
          if (cart) updateCount(cart.item_count);
        }
        announce();
        scheduleResync();
      })
      .catch(function () {
        st.inFlight = false;
        delete lines[key];
        resyncOnce(); // restore truth from server
      });
  }

  function queueChange(key, delta, absolute) {
    var st = lines[key];
    if (!st) {
      var el = itemEl(key);
      var num = el ? el.querySelector('.bbw-cart__qty-num') : null;
      var current = num ? parseInt(num.textContent, 10) || 0 : 0;
      st = lines[key] = { desired: current, inFlight: false, sendTimer: null };
    }
    st.desired = absolute !== undefined ? absolute : Math.max(0, st.desired + delta);
    renderOptimistic(key);
    // Collapse rapid clicks into one request; removals send immediately.
    if (st.sendTimer) clearTimeout(st.sendTimer);
    var wait = st.desired === 0 ? 0 : 220;
    st.sendTimer = window.setTimeout(function () {
      st.sendTimer = null;
      sendLine(key);
    }, wait);
  }

  /* ── Add to cart: intercept the redesign ATC forms ─────────
     Capture phase + stopImmediatePropagation so we win over ANY
     theme handler that might let the form post natively. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-bbw-add]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var btn = form.querySelector('button[type="submit"]');
    if (btn && btn.disabled) return;
    var label = btn ? btn.querySelector('.bbw-atc__label') : null;
    var prevLabel = label ? label.textContent : '';
    var fd = new FormData(form);
    fd.append('sections', SECTION);
    fd.append('sections_url', window.location.pathname);
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    if (label) label.textContent = 'ADDING…';

    var my = ++seq;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: fd
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) return; // e.g. sold out / over stock
        if (my === seq && res.data.sections) applySections(res.data.sections);
        open();
        announce();
        scheduleResync();
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          if (my === seq && cart) updateCount(cart.item_count);
        });
      })
      .catch(function () {})
      .finally(function () {
        if (label && prevLabel) label.textContent = prevLabel;
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
      });
  }, true);

  /* ── Delegated clicks (open / close / qty / remove) ──────── */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      open();
      return;
    }

    var r = root();
    if (!r || !r.contains(e.target)) return;

    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); close(); return; }

    var inc = e.target.closest('[data-qty-inc]');
    var dec = e.target.closest('[data-qty-dec]');
    var rem = e.target.closest('[data-cart-remove]');
    if (!inc && !dec && !rem) return;

    e.preventDefault();
    var trigger = inc || dec || rem;
    var key = trigger.getAttribute('data-key');

    if (rem) queueChange(key, 0, 0);
    else if (inc) queueChange(key, 1);
    else if (dec) queueChange(key, -1);
  });

  /* ── Legacy pages: react to the theme's cart:update event ──
     Legacy ATC flows (Horizon product-form, quick-add, sticky
     CTA…) dispatch document-level 'cart:update' after adding.
     We refresh + open; sequencing prevents double-render races
     with our own add path. */
  var refreshing = false;
  function refreshAndOpen() {
    if (refreshing) return;
    refreshing = true;
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq) applySections(sections); })
      .catch(function () {})
      .finally(function () {
        open();
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { if (cart) updateCount(cart.item_count); })
          .catch(function () {})
          .finally(function () { refreshing = false; });
      });
  }
  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.source === 'bbw-cart') return; // our own announcement
    refreshAndOpen();
    scheduleResync();
  });

  /* ── Escape closes ────────────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();
